import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SendIcon from "@mui/icons-material/Send";
import { toast } from "react-toastify";
import { submit_general_enquiry } from "../../api/ControllApi";
import { useUser } from "../../context/LoginDetail";

export default function GeneralEnquiryModal({ open, onClose }) {
  const { user } = useUser();

  const [formData, setFormData] = useState({
    full_name: "",
    phone_number: "",
    email_address: "",
    enquiry: "",
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData({
        full_name: user?.name || "",
        phone_number: user?.phone || "",
        email_address: user?.email || "",
        enquiry: "",
      });
      setErrors({});
    }
  }, [open, user]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.full_name.trim()) {
      newErrors.full_name = "Full Name is required";
    }
    if (!formData.phone_number.trim()) {
      newErrors.phone_number = "Phone number is required";
    } else if (formData.phone_number.trim().length < 10) {
      newErrors.phone_number = "Please enter a valid 10-digit phone number";
    }
    if (!formData.enquiry.trim()) {
      newErrors.enquiry = "Please enter your enquiry message";
    } else if (formData.enquiry.trim().length < 5) {
      newErrors.enquiry = "Enquiry must be at least 5 characters long";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const res = await submit_general_enquiry({
        full_name: formData.full_name.trim(),
        phone_number: formData.phone_number.trim(),
        email_address: formData.email_address.trim() || undefined,
        enquiry: formData.enquiry.trim(),
      });

      if (res?.status === 200 || res?.status === true) {
        toast.success(res?.message || "Enquiry submitted successfully! Our team will contact you soon.");
        onClose();
      } else {
        toast.error(res?.message || "Failed to submit enquiry. Please try again.");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Failed to submit enquiry. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3.5,
          p: 1,
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2.5, pb: 1.5, display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              bgcolor: "#FFECEC",
              color: "#FF5757",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AutoAwesomeIcon sx={{ fontSize: "1.4rem" }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: "#1F2937", lineHeight: 1.2 }}>
              General Enquiry
            </Typography>
            <Typography variant="body2" sx={{ color: "#6B7280", mt: 0.3, fontSize: "0.83rem" }}>
              Have questions or need assistance? Leave a message below.
            </Typography>
          </Box>
        </Box>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={submitting}
          sx={{ color: (theme) => theme.palette.grey[400], mt: -0.5, mr: -0.5 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2.5, pt: 1 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2.2, mt: 1 }}>
          <TextField
            label="Full Name"
            fullWidth
            required
            size="medium"
            value={formData.full_name}
            onChange={handleChange("full_name")}
            error={Boolean(errors.full_name)}
            helperText={errors.full_name}
            placeholder="Enter your full name"
            disabled={submitting}
            InputProps={{
              sx: { borderRadius: 2 },
            }}
          />

          <TextField
            label="Phone Number"
            fullWidth
            required
            size="medium"
            value={formData.phone_number}
            onChange={handleChange("phone_number")}
            error={Boolean(errors.phone_number)}
            helperText={errors.phone_number}
            placeholder="Enter your mobile number"
            disabled={submitting}
            InputProps={{
              sx: { borderRadius: 2 },
            }}
          />

          <TextField
            label="Email Address (Optional)"
            fullWidth
            type="email"
            size="medium"
            value={formData.email_address}
            onChange={handleChange("email_address")}
            placeholder="Enter your email address"
            disabled={submitting}
            InputProps={{
              sx: { borderRadius: 2 },
            }}
          />

          <TextField
            label="Your Enquiry / Requirement"
            fullWidth
            required
            multiline
            rows={4}
            value={formData.enquiry}
            onChange={handleChange("enquiry")}
            error={Boolean(errors.enquiry)}
            helperText={errors.enquiry || "Provide details about your query or property requirements"}
            placeholder="What would you like to know or enquire about?"
            disabled={submitting}
            InputProps={{
              sx: { borderRadius: 2 },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, pt: 0, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
        <Button
          onClick={onClose}
          disabled={submitting}
          variant="outlined"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            px: 3,
            borderColor: "#D1D5DB",
            color: "#4B5563",
            fontWeight: 600,
            "&:hover": { borderColor: "#9CA3AF", bgcolor: "#F9FAFB" },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          variant="contained"
          disableElevation
          endIcon={!submitting && <SendIcon sx={{ fontSize: "1rem !important" }} />}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            px: 3.5,
            py: 1,
            bgcolor: "#FF5757",
            color: "#FFFFFF",
            fontWeight: 800,
            fontSize: "0.95rem",
            boxShadow: "0 4px 14px rgba(255, 87, 87, 0.4)",
            "&:hover": { bgcolor: "#E04848" },
          }}
        >
          {submitting ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={18} sx={{ color: "#fff" }} />
              <span>Submitting...</span>
            </Box>
          ) : (
            "Submit Enquiry"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
